import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import { toastError } from '@futrli/ui-components';

import { DriverCreatingWizardScreen } from './screens/driver-creating-wizard-screen';
import { DriverCreatedWizardScreen } from './screens/driver-created-wizard-screen';
import { ExcelDriverWizardScreen } from './screens/excel-driver-wizard-screen';
import { FormulaDriverWizardScreen } from './screens/formula-driver-wizard-screen';
import { FreestyleDriverWizardScreen } from './screens/freestyle-driver-wizard-screen';
import { InputMethod } from '../../../ui/data-builder/types';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { useDriverBuilderContext } from '../../contexts/driver-builder-context/driver-builder-context';

interface CreateDriverWizardContainerProps {
  onClose: () => void;
  onReset: () => void;
}

export const CreateDriverWizardContainer = ({ onClose, onReset }: CreateDriverWizardContainerProps) => {
  const intl = useIntl();

  const { formulaForm, freestyleForm, sharedForm, view } = useDriverBuilderContext();
  const { getRequestData: getSharedRequestData, inputMethod, validate: validateSharedForm } = sharedForm;
  const { getRequestData: getFormulaRequestData, updateRestoreFormulaString, validateFormula } = formulaForm;
  const { getRequestData: getFreestyleRequestData } = freestyleForm;
  const {
    errorLoading,
    formulaDriver,
    freestyleDriver,
    isLoading,
    isCreatingDriver,
    isUpdatingDriver,
    newDriverId,
    updatedDriverId
  } = view.state;

  const screen = (() => {
    if (isLoading) {
      return 'loading';
    }
    if (isCreatingDriver || isUpdatingDriver) {
      return 'creating';
    }
    if (newDriverId || updatedDriverId) {
      return 'created';
    }
    if (inputMethod === InputMethod.FORMULA) {
      return 'formula';
    }
    if (inputMethod === InputMethod.EXCEL) {
      return 'excel';
    }
    return 'freestyle';
  })();

  const handleCreateAnother = useCallback(() => {
    // This will cause the view to be re-created with no driverId,
    // which will reset all state and return the user to the start of the wizard
    onReset();
  }, [onReset]);

  const handleCreateFormulaDriver = useCallback(async () => {
    if (validateSharedForm() && validateFormula()) {
      updateRestoreFormulaString();
      const data = {
        ...getSharedRequestData(),
        ...getFormulaRequestData()
      };
      const newDriver = await view.createFormulaDriver(data);
      trackEvent(TRACKING_EVENTS.CREATE_FORMULA_DRIVER);
      return newDriver;
    }
  }, [
    getFormulaRequestData,
    getSharedRequestData,
    updateRestoreFormulaString,
    validateFormula,
    validateSharedForm,
    view
  ]);

  const handleCreateFreestyleDriver = useCallback(async () => {
    if (validateSharedForm()) {
      const data = {
        ...getSharedRequestData(),
        ...getFreestyleRequestData()
      };
      const newDriver = await view.createFreestyleDriver(data);
      trackEvent(TRACKING_EVENTS.CREATE_FREESTYLE_DRIVER);
      return newDriver;
    }
  }, [getFreestyleRequestData, getSharedRequestData, validateSharedForm, view]);

  const handleUpdateFormulaDriver = useCallback(async () => {
    if (validateSharedForm() && validateFormula()) {
      updateRestoreFormulaString();
      const data = {
        ...getSharedRequestData(),
        ...getFormulaRequestData()
      };
      return await view.updateFormulaDriver(formulaDriver!.id, data);
    }
  }, [
    formulaDriver,
    getFormulaRequestData,
    getSharedRequestData,
    updateRestoreFormulaString,
    validateFormula,
    validateSharedForm,
    view
  ]);

  const handleUpdateFreestyleDriver = useCallback(async () => {
    if (validateSharedForm()) {
      const data = {
        ...getSharedRequestData(),
        ...getFreestyleRequestData()
      };
      return await view.updateFreestyleDriver(freestyleDriver!.id, data);
    }
  }, [freestyleDriver, getFreestyleRequestData, getSharedRequestData, validateSharedForm, view]);

  useEffect(() => {
    if (errorLoading) {
      toastError(
        intl.formatMessage({
          id: 'predict.error_loading_data',
          defaultMessage: 'An error occurred while loading the data from the server.'
        })
      );
      onClose();
    }
  }, [errorLoading, onClose, intl]);

  if (errorLoading) {
    return null;
  }

  return (
    <>
      {screen === 'loading' && <LoadingWizardScreen />}
      {screen === 'freestyle' && (
        <FreestyleDriverWizardScreen
          onClose={onClose}
          onCreate={handleCreateFreestyleDriver}
          onUpdate={handleUpdateFreestyleDriver}
        />
      )}
      {screen === 'formula' && (
        <FormulaDriverWizardScreen
          onClose={onClose}
          onCreate={handleCreateFormulaDriver}
          onUpdate={handleUpdateFormulaDriver}
        />
      )}
      {screen === 'excel' && <ExcelDriverWizardScreen onClose={onClose} />}
      {screen === 'creating' && <DriverCreatingWizardScreen isUpdate={isUpdatingDriver} />}
      {screen === 'created' && (
        <DriverCreatedWizardScreen
          isUpdate={!!updatedDriverId}
          onClose={onClose}
          onCreateAnother={handleCreateAnother}
        />
      )}
    </>
  );
};
